import { Notice } from '@verlo/shared/components/ui/notice';
import { nextBooking, untilWords, runsPast, clockPlus } from '../lib/freeUntil';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { origin } from '../components/BackBar';
import { tablesApi, reservationsApi, ordersApi, floorplanApi, menuApi } from '@verlo/shared/services/api';
import AllergyPicker from '../components/tables/AllergyPicker';
import OccasionPicker from '../components/tables/OccasionPicker';
import QuickSeatDialog from '../components/tables/QuickSeatDialog';
import { FloorplanView } from '../components/tables/FloorplanView';
import ManagerOverrideDialog from '../components/order/ManagerOverrideDialog';
import { statusConfig } from '../components/tables/tableConstants';
import TableCard from '../components/tables/TableCard';
import MoveTableDialog from '../components/tables/MoveTableDialog';
import TableRow, { TableRowHeader, sortTables } from '../components/tables/TableRow';
import TonightSheet from '../components/tables/TonightSheet';
import { floorTables, refusal } from '../lib/tonight';

import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Label } from '@verlo/shared/components/ui/label';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@verlo/shared/components/ui/tabs';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { toast } from 'sonner';
import { 
  Plus, Users, RefreshCw, AlertTriangle, Minus, CreditCard,
  Calendar
} from 'lucide-react';

// Status configuration and dining stages are now imported from ../components/tables/tableConstants

const TablesPage = () => {
  const navigate = useNavigate();
  // The tables are set up in the back office now (Admin › The tills › Floor plan). On the
  // till a manager changes only tonight: a table out of use, or tables joined (TonightSheet),
  // by holding the table. A server's hold does nothing.
  const { hasPermission } = useAuth();
  const isManager = hasPermission('floor.edit_floorplan');
  // The back office's plan, when there is one: its areas (an empty one still gets a tab).
  const [plan, setPlan] = useState(null);
  const [tonightTable, setTonightTable] = useState(null);
  const [tables, setTables] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  // The view (list / plan) and the section live in the URL, so coming back from a table
  // lands on the plan you left, and a screen can be pinned to one section.
  const [params, setParams] = useSearchParams();
  const selectedSection = params.get('section') || 'all';
  // Cards, compact rows, or the plan. With no choice made it's cards, whatever the size of
  // the floor: signing in lands on Tables, All, Cards (Oct 2026 sweep). It used to open
  // compact above twelve tables, so the same till opened differently venue to venue.
  const viewParam = params.get('view');
  const viewMode = viewParam === 'plan' ? 'map'
    : viewParam === 'compact' ? 'compact'
    : 'list';
  const setParam = (key, value, keep) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value == null || value === keep) next.delete(key); else next.set(key, value);
    return next;
  }, { replace: true });
  const setSelectedSection = (v) => setParam('section', v, 'all');
  const setViewMode = (v) => setParam('view', v === 'map' ? 'plan' : v === 'compact' ? 'compact' : 'cards', null);
  const here = () => ({ path: `/tables${window.location.search}`, label: viewMode === 'map' ? 'Floor plan' : 'Tables' });
  
  // Open table dialog state
  const [openTableDialog, setOpenTableDialog] = useState(false);
  // The menu, only for the seat sheet's "23 of 51 dishes contain it". Loaded the first time
  // the sheet opens; a failure just hides the count.
  const [menuForCounts, setMenuForCounts] = useState(null);
  useEffect(() => {
    if (!openTableDialog || menuForCounts) return;
    menuApi.getAll().then((r) => setMenuForCounts(r.data || [])).catch(() => setMenuForCounts([]));
  }, [openTableDialog, menuForCounts]);
  const [tableToOpen, setTableToOpen] = useState(null);
  // A reserved table tapped on the floor: the booking it is held for, to seat them or not.
  const [held, setHeld] = useState(null);   // { table, booking }
  const [openTableData, setOpenTableData] = useState({
    covers: 2,
    has_allergies: false,
    allergy_notes: '',
    allergens: [],
    occasion: '',
    occasion_notes: '', // For custom "Other" occasion
    time_limit_minutes: null,
  });
  
  // Status change validation state
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState({ table: null, status: null, balance: 0 });
  const [managerOverrideOpen, setManagerOverrideOpen] = useState(false);
  
  // Upcoming reservations state
  const [upcomingReservations, setUpcomingReservations] = useState([]);

  // The party being moved from a card's Move button (the dialog's own page does the same).
  const [moving, setMoving] = useState(null);
  // The compact view's sort: Since or Time, either way, or the floor's own order.
  const [rowSort, setRowSort] = useState(null);
  // Seat a walk-in: how many, for how long, then the tables free long enough.
  const [quickSeatOpen, setQuickSeatOpen] = useState(false);
  // Only the first load shows the skeleton. The 30-second refresh used to as well, which
  // swapped the whole floor out for placeholders and back: the page lost its scroll and
  // jumped to the top every half minute, most noticeably on a till nobody was touching.
  // Only the newest read lands: the 30-second poll can start before a change (a join, out
  // of use) and answer after the read that follows it, putting the floor back as it was.
  const fetchSeq = useRef(0);
  const fetchTables = useCallback(async () => {
    const mine = ++fetchSeq.current;
    try {
      const [tablesRes, featuresRes, planRes] = await Promise.all([
        tablesApi.getAll(), floorplanApi.features(), floorplanApi.plan().catch(() => null),
      ]);
      if (mine !== fetchSeq.current) return;
      setTables(tablesRes.data);
      setFeatures(featuresRes.data);
      if (planRes) setPlan(planRes.data);
    } catch (err) {
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  // Fetch upcoming reservations
  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const res = await reservationsApi.getByDate(today);
        // Filter to upcoming and sort by time
        const now = new Date().toTimeString().slice(0, 5);
        const upcoming = res.data
          .filter(r => r.status === 'confirmed' && r.time >= now)
          .sort((a, b) => a.time.localeCompare(b.time))
          .slice(0, 4);
        setUpcomingReservations(upcoming);
      } catch (e) {
        // Silently fail - reservations panel is supplementary
      }
    };
    fetchReservations();
    const interval = setInterval(fetchReservations, 60000);
    return () => clearInterval(interval);
  }, []);

  // Areas of the room: the back office's plan names them (an empty one still gets a tab),
  // and whatever the tables and features say.
  const sections = ['all', ...new Set([
    ...(plan?.areas || []),
    ...tables.map((t) => t.section || 'Main'),
    ...features.map((f) => f.section || 'Main'),
  ])];
  const inSection = (t) => selectedSection === 'all' || (t.section || 'Main') === selectedSection;
  // The cards and rows: a join is one table, "T5 + T6" (lib/tonight.js). The plan draws
  // every table where it stands.
  const filteredTables = floorTables(tables).filter(inSection);
  const planTables = tables.filter(inSection);

  // Holding a table (cards, rows, the plan) is a manager's way into tonight's changes.
  const longPressTimer = useRef(null);
  const handleCardLongPressStart = (table) => {
    if (!isManager) return;
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = 'fired';
      setTonightTable(tables.find((t) => t.id === table.id) || table);
    }, 600);
  };
  const handleCardLongPressEnd = () => {
    if (longPressTimer.current && longPressTimer.current !== 'fired') {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // `preset` comes from Seat a walk-in: the guests and the length already chosen there.
  // `heldFor` is the booking a held table is waiting for, when a walk-in is seated there
  // instead (M6): the sheet says what happens to it.
  const [heldFor, setHeldFor] = useState(null);
  // A free table's next booking today (undefined while it's looked up, null for none): the
  // sheet says how long the table is free, and whether the sitting chosen runs past it.
  const [nextUp, setNextUp] = useState(undefined);
  const hhmmNow = () => new Date().toTimeString().slice(0, 5);
  // The table whose sheet is open: a late lookup for another table mustn't land on this one.
  const sheetFor = useRef(null);
  const openSeatSheet = (table, preset = {}) => {
    sheetFor.current = table.id;
    setTableToOpen(table);
    setHeldFor(preset.heldFor || null);
    setNextUp(undefined);
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    reservationsApi.getByDate(today)
      .then((r) => { if (sheetFor.current === table.id) setNextUp(nextBooking(r.data || [], table.id, hhmmNow())); })
      .catch(() => { if (sheetFor.current === table.id) setNextUp(undefined); });
    setOpenTableData({
      covers: preset.covers ? Math.min(preset.covers, table.capacity) : Math.min(2, table.capacity),
      has_allergies: false,
      allergy_notes: '',
      allergens: [],
      occasion: '',
      occasion_notes: '',
      time_limit_minutes: preset.minutes || (table.capacity <= 2 ? 60 : 90),
    });
    setOpenTableDialog(true);
  };

  // Click handler - go directly to order page
  const handleTableClick = (table) => {
    // Out of use, or joined behind another: a manager gets tonight's sheet, anyone else
    // the reason.
    const why = refusal(table, tables);
    if (why && table.status !== 'occupied') {
      if (isManager) setTonightTable(tables.find((t) => t.id === table.id) || table);
      else toast.info(why);
      return;
    }
    if (table.status === 'occupied' || table.current_order_id) {
      navigate(`/tables/${table.id}/order`, { state: origin(here().path, here().label) });
    } else if (table.status === 'cleaning') {
      // The one action a cleaning table has: clear it. Seating happens once it's
      // available, not instead of that step — see TableCard's "Mark clean".
      handleTableStatusChange(table, 'available');
    } else if (table.status === 'available') {
      openSeatSheet(table);
    } else if (table.status === 'reserved') {
      // It used to go straight to the order page, which for a table nobody is sat at is a
      // menu with "T6 reserved" over it and nothing to do: the tap seemed to do nothing.
      // A reserved table is held for a booking, so the question is whether they're here.
      if (!table.reservation_id) { openSeatSheet(table); return; }
      reservationsApi.getById(table.reservation_id)
        .then((r) => setHeld({ table, booking: r.data }))
        .catch(() => openSeatSheet(table));
    }
  };

  const seatHeldBooking = async () => {
    const { table, booking } = held;
    try {
      await reservationsApi.seat(booking.id, table.id);
      toast.success(`${booking.customer_name || 'Booking'} seated at ${table.name}`);
      setHeld(null);
      navigate(`/tables/${table.id}/order`, { state: origin(here().path, here().label) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't seat the booking");
    }
  };

  const handleOpenTable = async () => {
    if (!tableToOpen) return;
    try {
      // An allergy is anything tapped or written: there is no separate switch to forget.
      const has_allergies = openTableData.allergens.length > 0 || !!openTableData.allergy_notes.trim();
      const { data: opened } = await tablesApi.open(tableToOpen.id, { ...openTableData, has_allergies });
      toast.success(`${tableToOpen.name} opened`);
      // Only when the server says it released a booking: a table held for nobody, or a
      // booking that had already moved, releases nothing.
      if (opened?.released_booking_id) {
        toast.info(`${heldFor?.customer_name ? `${heldFor.customer_name}'s booking` : 'The booking held here'} is in Unassigned in Bookings, to give it another table.`, { duration: 8000 });
      }
      setOpenTableDialog(false);
      navigate(`/tables/${tableToOpen.id}/order`, { state: origin(here().path, here().label) });
    } catch (err) {
      // The server says why ("Table not found" when another till has changed the floor,
      // "already occupied" when someone seated it first), and that reason is what tells a
      // server to refresh rather than try again.
      toast.error(err?.response?.data?.detail || "Couldn't open the table");
    }
  };

  // Handle status change with validation
  const handleTableStatusChange = async (table, newStatus) => {
    if (newStatus === table.status) return;
    
    // If changing to occupied, open the table dialog
    if (newStatus === 'occupied' && table.status !== 'occupied') {
      openSeatSheet(table);
      return;
    }
    
    // If changing away from occupied, check for outstanding balance
    if (table.status === 'occupied' && (newStatus === 'available' || newStatus === 'cleaning')) {
      if (table.current_order_id) {
        try {
          const orderRes = await ordersApi.getById(table.current_order_id);
          const order = orderRes.data;
          const orderTotal = order.total || 0;
          
          // Check if there are items and unpaid balance
          if (order.items && order.items.length > 0 && orderTotal > 0) {
            // Calculate paid amount
            const paidAmount = order.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
            
            if (paidAmount < orderTotal) {
              // Has outstanding balance
              const remainingBalance = orderTotal - paidAmount;
              setPendingStatusChange({ table, status: newStatus, balance: remainingBalance });
              setStatusChangeDialogOpen(true);
              return;
            }
          }
        } catch (e) {
          // If can't check order, proceed anyway
          console.log('Could not fetch order:', e);
        }
      }
    }
    
    // No validation needed - proceed
    try {
      await tablesApi.updateStatus(table.id, newStatus);
      fetchTables();
      toast.success(`${table.name} marked as ${statusConfig[newStatus].label}`);
    } catch (err) {
      toast.error('Failed to update table status');
    }
  };

  // The dialog collects the manager + PIN; the cancel endpoint verifies it
  // (cancelling a fired order is a void). Let anything thrown surface in the dialog.
  const handleManagerOverrideFromTables = async ({ managerId, pin }) => {
    const { table, status } = pendingStatusChange;
    if (table.current_order_id) {
      await ordersApi.updateStatus(table.current_order_id, 'cancelled', {
        manager_id: managerId,
        manager_pin: pin,
      });
    }
    await tablesApi.updateStatus(table.id, status);
    toast.success(`Bill cleared by manager. ${table.name} marked as ${statusConfig[status].label}`);
    setPendingStatusChange({ table: null, status: null, balance: 0 });
    fetchTables();
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  // Stats
  const availableCount = tables.filter((t) => t.status === 'available' && !t.out_of_use).length;
  const occupiedCount = tables.filter((t) => t.status === 'occupied').length;
  const reservedCount = tables.filter((t) => t.status === 'reserved').length;
  const cleaningCount = tables.filter((t) => t.status === 'cleaning').length;

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="tables-page">
      {moving && (
        <MoveTableDialog
          open={!!moving}
          onOpenChange={(v) => { if (!v) setMoving(null); }}
          table={moving}
          onMoved={() => { setMoving(null); fetchTables(); }}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="text-2xl font-bold font-heading text-ink">Tables</h1>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-ink-faint hover:text-ink-soft"
              onClick={fetchTables}
              data-testid="refresh-tables"
              title="Refresh"
            >
              <RefreshCw size={15} />
            </Button>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-ink-soft">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white ring-1 ring-inset ring-ink-faint"></span>
              {availableCount} free
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-ink-soft"></span>
              {occupiedCount} seated
            </span>
            {reservedCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-verlo-hover"></span>
                {reservedCount} reserved
              </span>
            )}
            {cleaningCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-ink-faint"></span>
                {cleaningCount} cleaning
              </span>
            )}
            {/* Allergies are shown per-table, where a server acts on them — not as a
                venue-level tally, which isn't something you can do anything with. */}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Which view — a segmented control, like every other "show me it this way". */}
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: 'list', label: 'Cards', testId: 'tables-view-list' },
              { value: 'compact', label: 'Compact', testId: 'tables-view-compact' },
              { value: 'map', label: 'Floor plan', testId: 'tables-view-map' },
            ]}
          />
          
          {/* Seating a walk-in is what the floor does all night, so it has the main button.
              Adding tables is set up in the back office now, with the rest of the floor. */}
          <Button className="h-9 bg-verlo hover:bg-verlo-hover" onClick={() => setQuickSeatOpen(true)} data-testid="seat-walk-in-btn">
            <Users size={16} className="mr-1.5" /> Seat a walk-in
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <Tabs value={selectedSection} onValueChange={setSelectedSection}>
        <TabsList className="bg-paper">
          {sections.map((section) => (
            <TabsTrigger key={section} value={section} className="capitalize data-[state=active]:bg-white">
              {section}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Said once, to the people who'd go looking for Edit: where it went, and what's left here. */}
      {isManager && plan?.managed && viewMode === 'map' && (
        <Notice tone="info" word="Set-up" title="Tables and the floor plan are set up in the back office" data-testid="tables-set-up-notice">
          Under Admin › The tills › Floor plan; changes there reach this till within a minute. For tonight only, hold a table here to take it out of use or join it to another.
        </Notice>
      )}

      {/* List View */}
      {/* Every card the same height: the rows are equal and the cards stretch to fill them,
          so a seated table with an allergy and a free one line up across the grid. */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-3 items-stretch">
          {filteredTables.map((table) => (
            <TableCard
              key={table.id}
              table={table}
              onOpen={(t) => {
                // Swallow the click that a long-press (delete) fired.
                if (longPressTimer.current === 'fired') {
                  longPressTimer.current = null;
                  return;
                }
                handleTableClick(t);
              }}
              onMove={(t) => setMoving(t)}
              onLongPressStart={handleCardLongPressStart}
              onLongPressEnd={handleCardLongPressEnd}
            />
          ))}
        </div>
      )}

      {/* Compact View: one row a table */}
      {viewMode === 'compact' && (
        <div className="overflow-x-auto border border-hairline bg-white" data-testid="tables-compact">
          <div className="min-w-[720px]">
            <TableRowHeader sort={rowSort} onSort={setRowSort} />
            {sortTables(filteredTables, rowSort).map((table) => (
              <TableRow
                key={table.id}
                table={table}
                onOpen={(t) => {
                  if (longPressTimer.current === 'fired') {
                    longPressTimer.current = null;
                    return;
                  }
                  handleTableClick(t);
                }}
                onLongPressStart={handleCardLongPressStart}
                onLongPressEnd={handleCardLongPressEnd}
              />
            ))}
          </div>
        </div>
      )}

      {/* Floorplan View */}
      {viewMode === 'map' && (
        <FloorplanView
          tables={tables}
          features={features}
          filteredTables={planTables}
          selectedSection={selectedSection}
          handleTableClick={handleTableClick}
          onHold={isManager ? (t) => setTonightTable(t) : undefined}
        />
      )}

      {/* Open Table Dialog */}
      <Dialog open={openTableDialog} onOpenChange={setOpenTableDialog}>
        <DialogContent size="sheet" data-testid="seat-sheet">
          <DialogHeader>
            <DialogTitle>Open {tableToOpen?.name}</DialogTitle>
          </DialogHeader>
          {/* A table held for a booking (M6): seating anyone else here moves the booking off it. */}
          {tableToOpen?.status === 'reserved' && (
            <Notice tone="warn" word="Held" title={heldFor ? `Held for ${heldFor.customer_name || 'a booking'}, ${heldFor.party_size} at ${heldFor.time}` : 'Held for a booking'} data-testid="seat-sheet-held">
              Seating a walk-in here moves the booking to Unassigned in Bookings, to give it another table.
            </Notice>
          )}
          {/* How long a free table is free: its next booking today. */}
          {tableToOpen?.status !== 'reserved' && nextUp !== undefined && (nextUp ? (
            <Notice
              tone={runsPast(openTableData.time_limit_minutes, nextUp.time, hhmmNow()) ? 'warn' : 'info'}
              word="Free"
              title={`Free until ${nextUp.time}, ${untilWords(nextUp.time, hhmmNow())} from now`}
              data-testid="seat-sheet-free-until"
            >
              Booked then for {nextUp.customer_name || 'a booking'}, {nextUp.party_size}.
              {runsPast(openTableData.time_limit_minutes, nextUp.time, hhmmNow())
                && ` A ${openTableData.time_limit_minutes}-minute table runs to ${clockPlus(hhmmNow(), openTableData.time_limit_minutes)}, past it: make it shorter, or seat them at another table.`}
            </Notice>
          ) : (
            <p className="text-[13px] text-ink-soft" data-testid="seat-sheet-free-until">No more bookings on {tableToOpen?.name} today.</p>
          ))}
          <div className="space-y-4 py-2">
            {/* Covers */}
            <div className="space-y-2">
              <Label>Guests</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setOpenTableData(prev => ({ ...prev, covers: Math.max(1, prev.covers - 1) }))}
                  disabled={openTableData.covers <= 1}
                >
                  <Minus size={16} />
                </Button>
                <span className="text-2xl font-bold w-12 text-center">{openTableData.covers}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setOpenTableData(prev => ({ 
                    ...prev, 
                    covers: Math.min(tableToOpen?.capacity || 20, prev.covers + 1),
                    time_limit_minutes: prev.covers + 1 <= 2 ? 60 : 90
                  }))}
                  disabled={openTableData.covers >= (tableToOpen?.capacity || 20)}
                >
                  <Plus size={16} />
                </Button>
              </div>
              <p className="text-xs text-ink-soft">
                Time: {openTableData.time_limit_minutes} minutes • Max: {tableToOpen?.capacity}
              </p>
            </div>

            {/* Allergies: the 14 as taps, so the pad can check every dish against them */}
            <AllergyPicker
              allergens={openTableData.allergens}
              notes={openTableData.allergy_notes}
              menu={menuForCounts}
              onChange={({ allergens, notes }) => setOpenTableData(prev => ({ ...prev, allergens, allergy_notes: notes }))}
            />

            {/* Occasion: chips, like the allergies, and changeable later from the table */}
            <div className="space-y-2">
              <Label>Occasion <span className="font-normal text-ink-faint">(optional)</span></Label>
              <OccasionPicker
                occasion={openTableData.occasion}
                notes={openTableData.occasion_notes}
                onChange={({ occasion, notes }) => setOpenTableData((prev) => ({ ...prev, occasion, occasion_notes: notes }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTableDialog(false)}>Cancel</Button>
            <Button className="bg-verlo" onClick={handleOpenTable}>Open table</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A reserved table: seat the booking it's held for, or a walk-in in its place */}
      <Dialog open={!!held} onOpenChange={(v) => { if (!v) setHeld(null); }}>
        <DialogContent size="form" data-testid="held-table-dialog">
          <DialogHeader>
            <DialogTitle>{held?.table.name} is held for {held?.booking.customer_name || 'a booking'}</DialogTitle>
            <DialogDescription>
              {held?.booking.party_size} {held?.booking.party_size === 1 ? 'guest' : 'guests'} at {held?.booking.time}
            </DialogDescription>
          </DialogHeader>
          {(held?.booking.notes || held?.booking.special_requests) && (
            <Notice tone="info" word="Note">
              {[held.booking.notes, held.booking.special_requests].filter(Boolean).join(' · ')}
            </Notice>
          )}
          <div className="grid gap-2">
            <Button className="h-12 bg-verlo hover:bg-verlo-hover" onClick={seatHeldBooking} data-testid="seat-held-booking">
              <Users size={16} className="mr-2" /> They're here: seat {held?.booking.customer_name?.split(' ')[0] || 'the booking'}
            </Button>
            <Button
              variant="outline"
              className="h-12"
              onClick={() => { const { table: t, booking } = held; setHeld(null); openSeatSheet(t, { heldFor: booking }); }}
              data-testid="seat-walk-in-instead"
            >
              Seat a walk-in here instead
            </Button>
          </div>
          <p className="text-[12.5px] text-ink-soft">
            A walk-in here moves {held?.booking.customer_name?.split(' ')[0] || 'the booking'} to Unassigned in Bookings, to be given another table before {held?.booking.time}.
          </p>
        </DialogContent>
      </Dialog>

      <QuickSeatDialog
        open={quickSeatOpen}
        onOpenChange={setQuickSeatOpen}
        onPick={(t, covers, minutes) => {
          setQuickSeatOpen(false);
          openSeatSheet(tables.find((x) => x.id === t.id) || t, { covers, minutes });
        }}
      />

      {/* Status Change Validation Dialog */}
      <Dialog open={statusChangeDialogOpen} onOpenChange={setStatusChangeDialogOpen}>
        <DialogContent size="form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-signal-warn">
              <AlertTriangle size={20} />
              Outstanding Balance
            </DialogTitle>
            <DialogDescription>
              {pendingStatusChange.table?.name} has an unpaid balance of {formatCurrency(pendingStatusChange.balance)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-ink-soft">
              Items have been sent to the kitchen. Please choose how to proceed:
            </p>
            
            <div className="space-y-2">
              <Button 
                className="w-full h-12 justify-start gap-3 bg-signal-ok hover:bg-signal-ok"
                onClick={() => {
                  setStatusChangeDialogOpen(false);
                  // Navigate to table order page for payment
                  navigate(`/tables/${pendingStatusChange.table?.id}/order`, { state: origin(here().path, here().label) });
                }}
              >
                <CreditCard size={18} />
                <div className="text-left">
                  <div className="font-medium">Pay Balance</div>
                  <div className="text-xs opacity-80">Go to table to process payment</div>
                </div>
              </Button>
              
              <Button 
                variant="outline"
                className="w-full h-12 justify-start gap-3 border-signal-warn-line text-signal-warn hover:bg-signal-warn-tint"
                onClick={() => {
                  setStatusChangeDialogOpen(false);
                  setManagerOverrideOpen(true);
                }}
              >
                <AlertTriangle size={18} />
                <div className="text-left">
                  <div className="font-medium">Manager Override</div>
                  <div className="text-xs text-ink-soft">Clear bill with manager approval</div>
                </div>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => {
                setStatusChangeDialogOpen(false);
                setPendingStatusChange({ table: null, status: null, balance: 0 });
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manager Override Dialog (P5-MGRPIN: server-side PIN authorisation) */}
      <ManagerOverrideDialog
        open={managerOverrideOpen}
        onOpenChange={setManagerOverrideOpen}
        balance={pendingStatusChange.balance}
        siteId={pendingStatusChange.table?.site_id}
        onSubmit={handleManagerOverrideFromTables}
        confirmLabel="Authorize & Clear Bill"
        formatCurrency={formatCurrency}
      />

      {/* Upcoming Reservations Panel - Bottom of page */}
      {upcomingReservations.length > 0 && (
        <div 
          className="mt-6 bg-verlo/5 border border-verlo/20 rounded-xl p-4 cursor-pointer hover:bg-verlo/10 transition-colors"
          onClick={() => navigate('/bookings')}
          data-testid="upcoming-bookings-panel"
        >
          <div className="flex items-center mb-3">
            <span className="text-sm font-medium text-verlo flex items-center gap-2">
              <Calendar size={16} />
              Upcoming Bookings
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {upcomingReservations.map(res => (
              <div key={res.id} className="bg-white px-3 py-2 shrink-0 border border-hairline shadow-sm min-w-[140px]">
                <div className="font-bold text-lg text-verlo">{res.time}</div>
                <div className="text-sm text-ink-soft font-medium truncate">{res.customer_name}</div>
                <div className="text-xs text-ink-soft flex items-center gap-1 mt-1">
                  <Users size={10} /> {res.party_size} guests
                </div>
                {res.table_name ? (
                  <Badge className="mt-1.5 bg-verlo text-white text-xs">{res.table_name}</Badge>
                ) : (
                  <Badge variant="outline" className="mt-1.5 text-xs text-signal-warn border-signal-warn-line bg-signal-warn-tint">Unassigned</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <TonightSheet table={tonightTable} tables={tables} onClose={() => setTonightTable(null)} onChanged={fetchTables} />
    </div>
  );
};

export default TablesPage;
